import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import { StateSelection, Loader } from "@components/common";
import classNames from "classnames";
import MaskedInput from "react-text-mask";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { ContactService, AccountService, ClientService } from "@services";
import { MASK_VALUE } from "@constants";
import utils from "@utils";
import Modal from "../Modal/Modal";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";

import "./AddContactModal.scss";

class AddContactModal extends Component {
	constructor(props) {
		super(props);
		this.initialState = {
			Id: undefined,
			FirstName: "",
			LastName: "",
			EmailAddress: "",
			MobilePhone: "",
			StreetAddress1: "",
			City: "",
			StateId: "",
			Zipcode: "",
			role: "",
			IsAdmin: false,
			IsCoordinator: false,
			IsViewOnly: false,
			organizationId: undefined,
			clientId: undefined,
			userActivated: false,
			loading: false,
			IsEditableContact: true,
			loadingPrimaryContact: false,
			editMode: false,
			errors: {
				firstNameError: false,
				lastNameError: false,
				emailError: false,
				phoneError: false,
			},
		};
		this.state = { ...this.initialState };
		this._confirmationModal = createRef();
	}

	onEnter = () => {
		let roleType;
		const { contact, isFromStaff, clientId, organizationId } = this.props;

		// roleType is defined by isAdmin and isCoordinator values and then assigned within the setState call below.
		if (Object.keys(contact).length > 0 || clientId || organizationId) {
			roleType =
				(contact.IsViewOnly === true && "View Only") ||
				(contact.IsAdmin === true && "Administrator") ||
				(contact.IsCoordinator === true && "Coordinator") ||
				(contact.IsCoordinator === false && contact.IsAdmin === false && "Staff") ||
				"";

			this.setState({
				Id: contact.Id || undefined,
				FirstName: contact.FirstName || "",
				LastName: contact.LastName || "",
				EmailAddress: contact.EmailAddress || "",
				MobilePhone: contact.MobilePhone || "",
				StreetAddress1: contact.StreetAddress1 || "",
				City: contact.City || "",
				StateId: contact.StateId || "",
				Zipcode: contact.Zipcode || "",
				IsAdmin: contact.IsAdmin || false,
				IsCoordinator: contact.IsCoordinator || false,
				IsViewOnly: contact.IsViewOnly || false,
				role: roleType || contact.role || "",
				organizationId: organizationId || undefined,
				clientId: clientId || undefined,
				userActivated: contact.UserActivated || false,
				IsEditableContact:
					contact.IsEditableContact === undefined || isFromStaff
						? true
						: contact.IsEditableContact,
				editMode: contact.Id !== undefined,
			});
			// editMode is based on if a contact with an id was provided, which is used to make proper Network requests when submitting the form.
			// IsEdittableContact is disabled in the staff page since its not needed there.
		}
	};

	// Input change functionality for all fields using their names except for role, which is set using isAdmin and isCoordinator values mentioned above.

	onInputChange = event => {
		const {
			target: { name },
		} = event;
		let {
			target: { value },
		} = event;
		if (name === "Zipcode") {
			value = value.replace(/\D/g, "");
		}
		if (name === "role") {
			this.setState({
				IsAdmin: value === "Administrator" || value === "View Only",
				IsCoordinator: value === "Coordinator",
				IsViewOnly: value === "View Only",
				role: value,
			});
		}
		this.setState({ [name]: value });
	};

	// onClose() which sets state to initial state when the modal closes and depending on the props passed, we call appropriate callback functions.

	onCloseModal = () => {
		const { closeModal, getContacts, updateOnClose } = this.props;

		this.setState({
			...this.initialState,
			loading: false,
			errors: {
				firstNameError: false,
				lastNameError: false,
				emailError: false,
				phoneError: false,
			},
		});

		if (updateOnClose) {
			getContacts();
		}

		closeModal();
	};

	// Using the id in our local state we are able to send the Registration Email.
	registerAccount = () => {
		const { lang } = global;
		const { Id } = this.state;
		AccountService.register(Id)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Registration email sent"),
					timeOut: 1500,
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error sending registration email"),
					timeOut: 1500,
				});
			});
	};

	// User is able to reset the password of the contact using the email in the state.

	resetStaffPassword = () => {
		const { lang } = global;
		const { EmailAddress } = this.state;
		AccountService.resetPassword(EmailAddress)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Password successfully reset"),
					timeOut: 1500,
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error resetting password"),
					timeOut: 1500,
				});
			});
	};

	/* In order to merge contacts that have similar emails, we use the function below to make the network request.
	We provide the API with clientId and ContactId in order to merge and then we use submitted event to update existing client.
	submittedEvent is "updateContactsForExistingClient()" from clientForm.JS */

	performMergeContact = ({ Id: contactId }) => {
		const { lang } = global;
		const { submittedEvent, clientId } = this.props;
		const { editMode } = this.state;
		const params = {
			Id: contactId,
			clientId,
		};

		ContactService.mergeContact(params)
			.then(response => {
				utils.notify({
					type: "success",
					message: lang.t("Contact successfully merged"),
					timeOut: 1500,
				});

				if (submittedEvent) {
					submittedEvent(response, editMode);
				}
				this.onCloseModal();
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Unable to merge contact"),
					timeOut: 1500,
				});
			});
	};

	openResetPasswordModal = () => {
		const { lang } = global;
		const { FirstName, EmailAddress } = this.state;
		this._confirmationModal.openModal({
			title: lang.t("Reset Password"),
			message: [
				<div
					className="text-center"
					key="reset-pwd-confirm"
				>
					{lang.t("dynamic.Are you sure you want to reset the users password", {
						user: FirstName,
					})}
					<br />
					<em>
						{lang.t(
							"The staff member will receive an email to complete the reset process_",
						)}
					</em>
				</div>,
			],
			confirmButton: `${lang.t("Yes")}, ${lang.t("Reset Password")}`,
			onConfirm: () => this.resetStaffPassword(EmailAddress),
		});
	};

	// Form validations to ensure user does not leave the following fields empty or send wrong formatted Email.

	validateForm = () => {
		const { FirstName, LastName, EmailAddress, MobilePhone, errors } = this.state;

		let isValid = false;
		const mobilePhone = utils.revertPhoneMask(MobilePhone);

		errors.firstNameError = FirstName === "";
		errors.lastNameError = LastName === "";
		errors.phoneError = mobilePhone === "" || mobilePhone.length !== 10;
		errors.emailError = !/^\S+@\S+\.\S{2,}$/.test(EmailAddress);

		this.setState({ ...errors });

		isValid = Object.values(errors).filter(error => error === true).length === 0;

		return isValid;
	};

	// Setting the Primary Contact by using the client form from our props and then updating the client in our state using id.

	setPrimaryContact = () => {
		const { lang } = global;
		const { Id, EmailAddress, FirstName, LastName, MobilePhone } = this.state;
		const { updateClient, client, addContactModalType, getContacts, updateContacts } =
			this.props;

		this.setState({ loadingPrimaryContact: true });

		let primaryContactPhoneNumber = "";
		primaryContactPhoneNumber = utils.revertPhoneMask(MobilePhone);

		ClientService.save({
			...client,
			id: client.Id,
			PrimaryContactEmail: EmailAddress,
			PrimaryContactFirstName: FirstName,
			PrimaryContactId: Id,
			PrimaryContactLastName: LastName,
			PrimaryContactPhoneNumber: primaryContactPhoneNumber,
		})
			.then(response => {
				if (addContactModalType) {
					updateContacts({ pageNumber: 0 }, () => getContacts());
				}
				if (updateClient) {
					updateClient(client.Id);
				}

				this.setState({ loadingPrimaryContact: false });

				if (response.data) {
					utils.notify({
						type: "success",
						message: lang.t("Primary contact updated successfully"),
						timeOut: 1500,
					});
				}
			})
			.catch(() => {
				this.setState({ loadingPrimaryContact: false });
				utils.notify({
					type: "error",
					message: lang.t("Unable to set primary contact"),
					timeOut: 1500,
				});
			});
	};

	// If you are using the modal from staffList, the modal directly adds or edits the staff list using onSaveNewStaff().

	onFormSubmit = event => {
		const { lang } = global;
		const { onSaveNewStaff, onSubmit, submittedEvent } = this.props;
		const {
			Id,
			FirstName,
			LastName,
			EmailAddress,
			StreetAddress1,
			MobilePhone,
			City,
			StateId,
			Zipcode,
			IsAdmin,
			IsCoordinator,
			IsViewOnly,
			clientId,
			organizationId,
		} = this.state;

		const { editMode } = this.state;
		event.preventDefault();

		const validation = this.validateForm();

		if (validation === true) {
			const params = {
				Id,
				FirstName,
				LastName,
				EmailAddress,
				StreetAddress1,
				MobilePhone: utils.revertPhoneMask(MobilePhone),
				City,
				StateId,
				Zipcode,
				IsAdmin,
				IsViewOnly,
				IsCoordinator,
				clientId,
				organizationId,
			};

			// updates client by creating temporary Contacts on the clientForm.
			if (onSubmit) {
				onSubmit(params);
				this.onCloseModal();
				return;
			}

			this.setState({
				loading: true,
			});
			ContactService.save(params)
				.then(response => {
					utils.notify({
						type: "success",
						message: lang.t(`Contact successfully ${editMode ? "updated" : "created"}`),
						timeOut: 1500,
					});
					if (onSaveNewStaff) {
						onSaveNewStaff(response);
					}

					this.setState({
						loading: false,
					});
					// ClientForm -> EditClient and then editing the contact will use this code to directly edit the contact using clientId.
					if (submittedEvent) {
						submittedEvent(response, editMode);
					}
					this.onCloseModal();
				})
				.catch(error => {
					const {
						response: { data, status },
					} = error;

					if (status === 409 && data && data.Data) {
						this._confirmationModal.openModal({
							title: "Merge Contact",
							message: data.Message,
							confirmButton: "Yes",
							onConfirm: () => this.performMergeContact(data.Data),
							onCancel: () => this.setState({ loading: false }),
						});
						return;
					}

					this.setState({ loading: false });
					if (params.clientId || params.organizationId) {
						utils.notify({
							type: "error",
							message:
								(data && lang.t("A contact with this email already exists")) ||
								lang.t(`Error ${editMode ? "updating" : "creating"} contact`),
							timeOut: 1500,
						});
					} else {
						utils.notify({
							type: "error",
							message: lang.t("A client must be selected before creating a contact"),
							timeOut: 1500,
						});
					}
				});
		}
	};

	render() {
		const { lang } = global;

		const {
			errors,
			Id,
			FirstName,
			LastName,
			EmailAddress,
			MobilePhone,
			StreetAddress1,
			City,
			StateId,
			role,
			Zipcode,
			userActivated,
			loading,
			IsEditableContact,
			loadingPrimaryContact,
			editMode,
		} = this.state;

		const { user, showModal, client, isFromStaff } = this.props;

		// Assigning Staff Roles based on user type below.

		const staffRoles = ["Staff", "Coordinator", "Administrator", "View Only"].filter(item =>
			user.isLEA() || user.isSuperAdmin() ? item : item !== "Coordinator",
		);

		return (
			<Modal
				show={showModal}
				onHide={() => this.onCloseModal()}
				onEnter={() => this.onEnter()}
				id="add-contact-modal"
			>
				<Modal.Header closeButton>
					<Modal.Title>
						{`${editMode ? "Edit" : "Add"} ${!isFromStaff ? "Contact" : "Staff"}`}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-horizontal">
						<div className="row form-group">
							<label
								htmlFor="firstName"
								className="col-sm-4 control-label"
							>
								{lang.t("First Name")}:
							</label>
							<div className="col-sm-6">
								<input
									name="FirstName"
									type="text"
									className={classNames("form-control", {
										"has-error": errors.firstNameError,
									})}
									value={FirstName}
									onChange={event => this.onInputChange(event)}
									disabled={IsEditableContact === false}
									data-test-id="act-contact-modal__first-name"
								/>
							</div>
						</div>

						<div className="row form-group">
							<label
								htmlFor="lastName"
								className="col-sm-4 control-label"
							>
								{lang.t("Last Name")}:
							</label>
							<div className="col-sm-6">
								<input
									name="LastName"
									type="text"
									className={classNames("form-control", {
										"has-error": errors.lastNameError,
									})}
									value={LastName}
									onChange={event => this.onInputChange(event)}
									disabled={IsEditableContact === false}
									data-test-id="act-contact-modal__last-name"
								/>
							</div>
						</div>

						<div className="row form-group">
							<label
								htmlFor="emailAddress"
								className="col-sm-4 control-label"
							>
								{lang.t("Email Address")}:
							</label>
							<div className="col-sm-6">
								<input
									name="EmailAddress"
									type="text"
									className={classNames("form-control", {
										"has-error": errors.emailError,
									})}
									value={EmailAddress}
									onChange={event => this.onInputChange(event)}
									disabled={IsEditableContact === false}
									data-test-id="act-contact-modal__address"
								/>
							</div>
						</div>

						<div className="row form-group">
							<label
								htmlFor="phoneNumber"
								className="col-sm-4 control-label"
							>
								{lang.t("Phone Number")}:
							</label>
							<div className="col-sm-6">
								<MaskedInput
									name="MobilePhone"
									type="tel"
									mask={MASK_VALUE.phoneMask}
									className={classNames("form-control", {
										"has-error": errors.phoneError,
									})}
									value={utils.formatPhoneNumber(MobilePhone)}
									onChange={event => this.onInputChange(event)}
									disabled={IsEditableContact === false}
									data-test-id="act-contact-modal__phone"
								/>
							</div>
						</div>

						<div className="row form-group">
							<label
								htmlFor=" streetAddress"
								className="col-sm-4 control-label"
							>
								{lang.t("Street Address")}:
							</label>
							<div className="col-sm-6">
								<input
									name="StreetAddress1"
									data-test-id="act-contact-modal__street-address"
									type="text"
									className="form-control"
									value={StreetAddress1}
									onChange={event => this.onInputChange(event)}
									disabled={IsEditableContact === false}
								/>
							</div>
						</div>

						<div className="row form-group">
							<label
								htmlFor="city"
								className="col-sm-4 control-label"
							>
								{lang.t("City")}:
							</label>
							<div className="col-sm-6">
								<input
									name="City"
									data-test-id="act-contact-modal__city"
									type="text"
									className="form-control"
									value={City}
									onChange={event => this.onInputChange(event)}
									disabled={IsEditableContact === false}
								/>
							</div>
						</div>

						<div className="row form-group">
							<label
								htmlFor="state"
								className="col-sm-4 control-label"
							>
								{lang.t("State")}:
							</label>
							<div className="col-sm-6">
								<StateSelection
									name="StateId"
									value={StateId}
									onChange={event => this.onInputChange(event)}
									disabled={IsEditableContact === false}
									dataTestId="act-contact-modal__state"
								/>
							</div>
						</div>

						<div className="row form-group">
							<label
								htmlFor="zipCode"
								className="col-sm-4 control-label"
							>
								{lang.t("Zip Code")}:
							</label>
							<div className="col-sm-6">
								<input
									name="Zipcode"
									type="tel"
									className="form-control"
									data-test-id="act-contact-modal__zip-code"
									maxLength={5}
									value={Zipcode}
									onChange={event => this.onInputChange(event)}
									disabled={IsEditableContact === false}
								/>
							</div>
						</div>
						<If condition={user.IsStaffAdmin && isFromStaff}>
							<div className="row form-group">
								<label
									className="col-sm-4 control-label"
									htmlFor="role"
								>{`${lang.t("Role")}:`}</label>
								<div className="col-sm-6">
									<select
										type="select"
										id="role"
										name="role"
										className="form-control"
										data-test-id="act-contact-modal__role"
										value={role}
										onChange={event => this.onInputChange(event)}
										disabled={Id === user.ContactId}
									>
										<For
											each="item"
											of={staffRoles}
										>
											<option
												key={`staff-roles_${item}`}
												value={item}
											>
												{item}
											</option>
										</For>
									</select>
								</div>
							</div>
						</If>
						<If condition={editMode && isFromStaff && user.IsStaffAdmin}>
							<div className="form-group">
								<div className="col-xs-12">
									<If condition={!userActivated}>
										<button
											type="button"
											className="btn btn-primary reset-password-btn"
											onClick={() => this.registerAccount()}
											data-test-id="act-contact-modal__resend-invite"
										>
											{lang.t("Resend Invite")}
										</button>
									</If>
								</div>
							</div>
						</If>
						<Loader active={loadingPrimaryContact} />
					</div>
				</Modal.Body>
				<Modal.Footer className="primary-save-cancel">
					<If condition={editMode && client && client.PrimaryContactId !== Id}>
						<button
							type="button"
							className="btn btn-primary primary-contact-btn"
							onClick={this.setPrimaryContact}
							data-test-id="act-contact-modal__set-primary-contact"
						>
							Set as Primary Contact
						</button>
					</If>
					<button
						type="button"
						onClick={this.onCloseModal}
						className="btn btn-link cancel-btn"
						data-test-id="act-contact-modal__cancel"
						title="Cancel"
					>
						{lang.t("Cancel")}
					</button>

					<button
						type="button"
						onClick={this.onFormSubmit}
						className="btn btn-primary save-btn"
						title="Save"
						data-test-id="act-contact-modal__save"
					>
						<Choose>
							<When condition={loading}>
								<i className="glyphicon glyphicon-refresh rkf-spin" />
							</When>
							<Otherwise>{lang.t("Save")}</Otherwise>
						</Choose>
					</button>
				</Modal.Footer>
				<ConfirmationModal
					ref={ref => {
						this._confirmationModal = ref;
					}}
				/>
			</Modal>
		);
	}
}

AddContactModal.propTypes = {
	user: PropTypes.object,
	clientId: PropTypes.number,
	contact: PropTypes.object,
	addContactModalType: PropTypes.string,
	submittedEvent: PropTypes.func,
	isFromStaff: PropTypes.bool,
	onSubmit: PropTypes.func,
	client: PropTypes.object,
	onSaveNewStaff: PropTypes.func,
	updateClient: PropTypes.func,
	updateContacts: PropTypes.func,
	showModal: PropTypes.bool,
	closeModal: PropTypes.func,
	getContacts: PropTypes.func,
	updateOnClose: PropTypes.bool,
	organizationId: PropTypes.number,
};
AddContactModal.defaultProps = {
	user: {
		isLEA: () => false,
		isSuperAdmin: () => false,
		IsStaffAdmin: false,
	},
	contact: {},
	onSaveNewStaff: undefined,
	isFromStaff: false,
	updateClient: undefined,
	updateContacts: undefined,
	onSubmit: undefined,
	submittedEvent: undefined,
	client: undefined,
	addContactModalType: undefined,
	clientId: undefined,
	showModal: false,
	closeModal: undefined,
	getContacts: undefined,
	updateOnClose: undefined,
	organizationId: undefined,
};

export default withLDConsumer()(AddContactModal);
